import { useState, useEffect, useRef } from "react";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import Api from "../api/api";
import { ReportsCharts } from "./reportsCharts";
import { dateRange, toDateString } from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "./reportsEnums";
import { saveAs } from 'file-saver';
import { EmptyState } from "../dashboard/emptyState";
import Loader from "../shared/loader";
import { createGroupedChartTraces } from "./chartUtils";
import { Link, useLocation } from "react-router-dom/cjs/react-router-dom";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import { VoidsReportFiltersModal } from "./filters-modals/voidsReportFiltersModal";
import { VoidsScheduleModal } from "./schedule-modals/voidsScheduleModal";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
const groupByLabel = (value) => value === 1 ? "Site" : value === 2 ? "Reason" : value === 3 ? "Tag" : "None"


export const VoidsReport = ({ sites, clients, tags, organisationId, users, token, organisationName, organisationLogo }) => {
    const location = useLocation()

    const [reportsData, setReportsData] = useState(null);
    const [fromDate, setFromDate] = useState(location && location.state !== undefined && location.state.fromDate !== undefined ? location.state.fromDate : dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(location && location.state !== undefined && location.state.toDate !== undefined ? location.state.toDate : dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(location && location.state !== undefined && (location.state.fromDate !== undefined || location.state.toDate !== undefined) ? null : reportDatePresetByLabel["Last month"]);
    const [selectedClient, setSelectedClient] = useState("");
    const [sitesByClient, setSitesByClient] = useState(sites)
    const [selectedSiteByClient, setSelectedSiteByClient] = useState("")
    const [selectedUser, setSelectedUser] = useState("")
    const [chartStyle, setChartStyle] = useState("bar");

    const [voidsGroupBy, setVoidsGroupBy] = useState(0)
    const [selectedTags, setSelectedTags] = useState([])
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(null);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const reportsLandingUrl = `/organisation/${ organisationId }/reports`;
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const pdfRef = useRef()

    const pdfClientName = (clientId) => {
        if (!stringHelpers.isNullOrEmpty(clientId)) {
            const client = clients.find(item => item.id === clientId)
            if (client !== undefined) {
                return client.name
            }
        }
        return "All"
    }

    const pdfSiteName = (siteId) => {
        if (!stringHelpers.isNullOrEmpty(siteId)) {
            const site = sitesByClient.find(item => item.id === siteId)
            if (site !== undefined) {
                return site.name
            }
        }
        return "All"
    }

    const pdfUserName = (userId) => {
        if (!stringHelpers.isNullOrEmpty(userId)) {
            const user = users.find(item => item.id === userId)
            if (user !== undefined) {
                return `${ user.firstName } ${ user.lastName }`
            }
        }
        return "All"
    }

    const pdfGroupByName = (groupBy) => groupByLabel(groupBy)

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setSelectedClient("");
        setSitesByClient(sites)
        setSelectedSiteByClient("")
        setReportsData(null);
        setVoidsGroupBy(0)
        setSelectedUser("")
        setSelectedTags([])
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Group By": "None"
        })
        setShowFilterPills(true)
        fetchVoidsReport(defaultRange.firstDate, defaultRange.lastDate, "", "", "", 0, []);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Date Range": {
                const defaultRange = dateRange("lastMonth")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["Last month"])
                fetchVoidsReport(defaultRange.firstDate, defaultRange.lastDate, selectedSiteByClient, selectedClient, selectedUser, voidsGroupBy, selectedTags)
                break
            }
            case "Client":
                setSelectedClient("")
                setSitesByClient(sites)
                setSelectedSiteByClient("")
                fetchVoidsReport(fromDate, toDate, "", "", selectedUser, voidsGroupBy, selectedTags)
                break
            case "Site":
                setSelectedSiteByClient("")
                fetchVoidsReport(fromDate, toDate, "", selectedClient, selectedUser, voidsGroupBy, selectedTags)
                break
            case "User":
                setSelectedUser("")
                fetchVoidsReport(fromDate, toDate, selectedSiteByClient, selectedClient, "", voidsGroupBy, selectedTags)
                break
            case "Group By":
                setVoidsGroupBy(0)
                fetchVoidsReport(fromDate, toDate, selectedSiteByClient, selectedClient, selectedUser, 0, selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                fetchVoidsReport(fromDate, toDate, selectedSiteByClient, selectedClient, selectedUser, voidsGroupBy, [])
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "Date Range") {
                const defaultRange = dateRange("lastMonth")
                updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
            } else if (closedLabel === "Client") {
                updatedFilters.Client = null
                updatedFilters.Site = null
            } else if (closedLabel === "Group By") {
                updatedFilters["Group By"] = "None"
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                fetchVoidsReport(fromDate, toDate, selectedSiteByClient, selectedClient, selectedUser, voidsGroupBy, remainingIds)
                break
            default:
                break
        }
    }

    const fetchVoidsReport = (from, to, siteId, clientId, userId, groupBy, tagIds) => {
        setIsLoading(true)
        Api.getVoidsReport(
            data => {
                setReportsData(data)
                setTotalCount(stringHelpers.formatDecimal(data.reduce((acc, curr) => { acc += curr.count; return acc }, 0), 0))
                setIsLoading(false)
            },
            error => {
                console.log(error);
                setIsLoading(false)
            },
            from,
            to,
            !stringHelpers.isNullOrEmpty(siteId) ? siteId : null,
            !stringHelpers.isNullOrEmpty(clientId) ? clientId : null,
            !stringHelpers.isNullOrEmpty(userId) ? userId : null,
            0,
            groupBy,
            tagIds,
            token,
            organisationId
        )
    }

    const handleDownload = () => {
        Api.getVoidsReport(
            (blob, filename) => { saveAs(blob, filename) },
            error => {
                console.log(error);
                setIsLoading(false)
            },
            fromDate,
            toDate,
            !stringHelpers.isNullOrEmpty(selectedSiteByClient) ? selectedSiteByClient : null,
            !stringHelpers.isNullOrEmpty(selectedClient) ? selectedClient : null,
            !stringHelpers.isNullOrEmpty(selectedUser) ? selectedUser : null,
            1,
            voidsGroupBy,
            selectedTags,
            token,
            organisationId
        )
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        siteId: selectedSiteByClient,
        clientId: selectedClient,
        userId: selectedUser,
        voidGroupBy: voidsGroupBy,
        tagIds: selectedTags,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchVoidsReport(fromDate, toDate, selectedSiteByClient, selectedClient, selectedUser, voidsGroupBy, selectedTags);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Group By": groupByLabel(voidsGroupBy)
        });
        setShowFilterPills(true);
    }, [])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(reportsData)) {
            if (voidsGroupBy === 0) {
                const reasonOptions = reportsData.reduce((acc, curr) => {
                    if (!acc.includes(curr.name)) acc.push(curr.name);
                    return acc;
                }, [])

                const countsBySiteName = reportsData.reduce((acc, curr) => {
                    acc[curr.siteName] = {
                        ...acc[curr.siteName],
                        [`${ curr.name }`]: curr.count
                    }
                    return acc
                }, {})
                setChartData(createGroupedChartTraces(reasonOptions, countsBySiteName));
            }
            else if (voidsGroupBy === 1) {
                setChartData(reportsData.map(item => (
                    {
                        "label": item.siteName,
                        "Count": item.count,
                    }
                )))
            }
            else if (voidsGroupBy === 2) {
                setChartData(reportsData.map(item => (
                    {
                        "label": item.name,
                        "Count": item.count,
                    }
                )))
            }
            else if (voidsGroupBy === 3) {
                setChartData(reportsData.map(item => (
                    {
                        "label": item.tagName,
                        "Count": item.count,
                    }
                )))
            }
        }
    }, [reportsData, chartStyle])

    const selectedTagNames = !arrayUtilities.isNullOrEmpty(selectedTags) && !arrayUtilities.isNullOrEmpty(tags)
        ? selectedTags
            .map(selectedTagId => tags.find(tag => tag.id === selectedTagId))
            .filter(tag => tag !== undefined)
            .map(tag => tag.name)
            .join(", ")
        : "All"

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            Voids
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                            <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                        </div>
                                        <button
                                            onClick={ () => handleDownload() }
                                            className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                        >
                                            Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - Voids`) }
                                            className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                        >
                                            Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                        </button>
                                        <button
                                            onClick={ () => setShowScheduleExportModal(true) }
                                            className="btn btn-sm btn-secondary nowrap mr-2"
                                        >
                                            Schedule Report<i className="ml-2 far fa-clock"></i>
                                        </button>
                                        <Link
                                            className="btn btn-falcon-default btn-sm mr-3"
                                            type="button"
                                            to={ reportsLandingUrl }>
                                            <i className="fas fa-reply"></i> Back
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => {
                                                    const isResettable = label === "Date Range" || label === "Group By"
                                                    return <AppliedFilterPill
                                                        key={ label }
                                                        label={ label }
                                                        value={ value }
                                                        isResettable={ isResettable }
                                                        renderItemLabel={ renderTagFilterItemLabel }
                                                        onTextClick={ () => setShowFiltersModal(true) }
                                                        onCloseClick={ handleFilterPillClose }
                                                        onRemoveItem={ handleFilterPillItemRemove }
                                                    />
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Voids">
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Range</span>
                                            <span className="print-only-filter-value">
                                                { toDateString(fromDate) } - { toDateString(toDate) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Client</span>
                                            <span className="print-only-filter-value">
                                                { pdfClientName(selectedClient) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Site</span>
                                            <span className="print-only-filter-value">
                                                { pdfSiteName(selectedSiteByClient) }
                                            </span>
                                        </div>
                                        {
                                            users &&
                                            <div className="print-only-filter">
                                                <span className="print-only-filter-label">User</span>
                                                <span className="print-only-filter-value">
                                                    { pdfUserName(selectedUser) }
                                                </span>
                                            </div>
                                        }
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Group By</span>
                                            <span className="print-only-filter-value">
                                                { pdfGroupByName(voidsGroupBy) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Tags</span>
                                            <span className="print-only-filter-value">
                                                { selectedTagNames }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                {
                                    isLoading
                                        ? <Loader />
                                        : <>
                                            {
                                                !arrayUtilities.isNullOrEmpty(reportsData)
                                                    ? (
                                                        <div className="mt-3 d-flex flex-column">
                                                            <div className="pl-6 mb-3 print-hide">
                                                                <button
                                                                    className={ `btn btn-${ chartStyle === "bar" ? "dark" : "secondary" } btn-sm mr-1 nowrap` }
                                                                    onClick={ () => setChartStyle("bar") }><i className="fas fa-chart-bar mr-2"></i>Bar Graph
                                                                </button>
                                                                <button
                                                                    className={ `btn btn-${ chartStyle === "line" ? "dark" : "secondary" } btn-sm mr-1 ml-1 nowrap` }
                                                                    onClick={ () => setChartStyle("line") }><i className="fas fa-chart-line mr-2"></i>Line Graph
                                                                </button>
                                                            </div>
                                                            {
                                                                chartData &&
                                                                <ReportsCharts tooltipCustomValueLabel="Count" isGroupedChart={ voidsGroupBy === 0 } data={ chartData } chartStyle={ chartStyle } />
                                                            }
                                                            <div className="card-body bg-white border-top p-0">
                                                                <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                                    <thead className="bg-200 text-900">
                                                                        <tr>
                                                                            {
                                                                                voidsGroupBy === 0
                                                                                ? <>
                                                                                    <th>Site Name</th>
                                                                                    <th>Reason</th>
                                                                                    <th className="text-right">Count</th>
                                                                                </>
                                                                                : voidsGroupBy === 1
                                                                                    ? <>
                                                                                        <th>Site Name</th>
                                                                                        <th className="text-right">Count</th>
                                                                                    </>
                                                                                    : voidsGroupBy === 3
                                                                                    ? <>
                                                                                        <th>Tag</th>
                                                                                        <th className="text-right">Count</th>
                                                                                    </>
                                                                                    : <>
                                                                                        <th>Reason</th>
                                                                                        <th className="text-right">Count</th>
                                                                                    </>
                                                                            }
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            voidsGroupBy === 0
                                                                            ? reportsData.map(
                                                                                (item, index) => <tr key={ index }>
                                                                                    <td>
                                                                                        <Link
                                                                                            to={ { pathname: `/organisation/${ organisationId }/enforcement`, state: { siteId: item.siteId, status: 501 } } }>
                                                                                            <h6 className="my-0">{ item.siteName }</h6>
                                                                                        </Link>
                                                                                    </td>
                                                                                    <td>{ item.name }</td>
                                                                                    <td className="text-right">{ stringHelpers.formatDecimal(item.count, 0) }</td>
                                                                                </tr>
                                                                            )
                                                                            : voidsGroupBy === 1
                                                                            ? reportsData.map(
                                                                                (item, index) => <tr key={ index }>
                                                                                    <td>
                                                                                        <Link
                                                                                            to={ { pathname: `/organisation/${ organisationId }/enforcement`, state: { siteId: item.siteId, status: 501 } } }>
                                                                                            <h6 className="my-0">{ item.siteName }</h6>
                                                                                        </Link>
                                                                                    </td>
                                                                                    <td className="text-right">{ stringHelpers.formatDecimal(item.count, 0) }</td>
                                                                                </tr>
                                                                            )
                                                                            : voidsGroupBy === 3
                                                                            ? reportsData.map(
                                                                                (item, index) => <tr key={ index }>
                                                                                    <td>{ item.tagName }</td>
                                                                                    <td className="text-right">{ stringHelpers.formatDecimal(item.count, 0) }</td>
                                                                                </tr>
                                                                            )
                                                                            : reportsData.map(
                                                                                (item, index) => <tr key={ index }>
                                                                                    <td>{ item.name }</td>
                                                                                    <td className="text-right">{ stringHelpers.formatDecimal(item.count, 0) }</td>
                                                                                </tr>
                                                                            )
                                                                        }
                                                                        <tr className="font-weight-bold w-100">
                                                                            <td>Total</td>
                                                                            <td className="text-right" colSpan={ 3 }>
                                                                                { totalCount }
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    ) : <EmptyState message="No Data available for the selected filters" />
                                            }
                                        </>
                                }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <VoidsReportFiltersModal
                    showModal={ showFiltersModal }
                    handleClose={ () => setShowFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setSelectedClient(draftFilters.selectedClient)
                        setSitesByClient(draftFilters.sitesByClient)
                        setSelectedSiteByClient(draftFilters.selectedSiteByClient)
                        setSelectedUser(draftFilters.selectedUser)
                        setVoidsGroupBy(draftFilters.groupBy)
                        setSelectedTags(draftFilters.selectedTags)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchVoidsReport(draftFilters.fromDate, draftFilters.toDate, draftFilters.selectedSiteByClient, draftFilters.selectedClient, draftFilters.selectedUser, draftFilters.groupBy, draftFilters.selectedTags)
                    } }
                    initialFromDate={ fromDate }
                    initialToDate={ toDate }
                    initialDatePreset={ datePreset }
                    initialSelectedClient={ selectedClient }
                    initialSitesByClient={ sitesByClient }
                    initialSelectedSiteByClient={ selectedSiteByClient }
                    initialSelectedUser={ selectedUser }
                    initialVoidsGroupBy={ voidsGroupBy }
                    initialSelectedTags={ selectedTags }
                    initialAppliedFilters={ appliedFilters }
                    clients={ clients }
                    sites={ sites }
                    users={ users }
                    tags={ tags || [] }
                    organisationId={ organisationId }
                    token={ token }
                    key="voids-report-filters-modal"
                />
            }
            {
                showScheduleExportModal &&
                <VoidsScheduleModal
                    sites={ sites }
                    clients={ clients }
                    users={ users }
                    tags={ tags || [] }
                    organisationId={ organisationId }
                    token={ token }
                    initialParameters={ scheduleInitialParameters }
                    initialSitesByClient={ sitesByClient }
                    onCancel={ closeScheduleExportModal }
                    onSaved={ closeScheduleExportModal }
                />
            }
        </>
    )
}
